import React, { useState } from "react";
import { Modal } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import "./style.scss";
import SearchInputComponent from "../../../components/reusable-ui/search-input";
import { removeSpecialCharacters } from "../../../utils";
import { useDispatch } from "react-redux";
import { toastSuccess } from "../../../utils/react-toaster";
import { COMPONENT_MODES, CTA_SIZE, CTA_TYPE } from "../../../data/constants";
import CtaComponent from "../../../components/reusable-ui/cta";
import {
  deleteItemScheduleData,
  getAllItemSchedulesData,
} from "../../../redux/slices";
import ConfirmationModal from "../../../components/reusable-ui/confirmation-modal";
import ItemScheduleDetailsForm from "./item-schedule-details-form";
import emptyListPlaceholder from "../../../assets/images/empty-list-placeholder.svg";
import DescriptionText from "../../../components/reusable-ui/description-text";
import { CMS_DESCRIPTION_TEXTS } from "../../../data/cms-description-text";

interface Props {
  openItemScheduleView: any;
  data: any;
  updateItemScheduleDataInParentState?: any;
  onDiscardCtaClick?: any;
  onSaveCtaClick?: any;
  errorMessageMap?: any;
  isChangesMade?: boolean;
  modeConfig?: any;
  setModeConfig?: any;
  itemScheduleId?: string;
}

const ItemScheduleViewComponent = (props: Props) => {
  const {
    data,
    itemScheduleId,
    modeConfig,
    openItemScheduleView,
    updateItemScheduleDataInParentState,
    onDiscardCtaClick,
    onSaveCtaClick,
    setModeConfig,
    errorMessageMap,
    isChangesMade,
  } = props;
  const dispatch = useDispatch();
  const [searchData, setSearchData] = useState<any>({
    value: "",
    results: [],
  });

  const [openDeleteItemScheduleAlert, setOpenDeleteItemScheduleAlert] =
    useState(false);
  const [curItemScheduleId, setCurItemScheduleId] = useState("");
  const itemSchedulesDataMap = useSelector(
    (state: RootState) => state.itemSchedulesReducer.itemSchedulesDataMap
  );
  const allChannelsDefDataMap = useSelector(
    (state: RootState) => state.businessReducer.allChannelsDefDataMap
  );

  /**
   * On search input it will parse the itemSchedulesDataList and it will filter out all the itemSchedules based on the search string on name of the item schedule
   * @param searchStr
   */
  const onSearchInput = (searchStr: string) => {
    const results: any = [];
    if (searchStr && itemSchedulesDataMap) {
      for (let key of Object.keys(itemSchedulesDataMap)) {
        if (
          itemSchedulesDataMap[key].name
            .toLocaleLowerCase()
            .includes(searchStr.toLocaleLowerCase())
        ) {
          results.push(itemSchedulesDataMap[key]);
        }
      }
    }
    setSearchData({ value: searchStr, results });
  };

  const onItemScheduleClick = (itemScheduleId: string = "") => {
    openItemScheduleView(itemScheduleId);
  };

  const deleteItemSchedule = (mid: string) => {
    dispatch(
      deleteItemScheduleData({
        mid,
        callback: (isSuccess: Boolean) => {
          if (isSuccess) {
            toastSuccess("Item Schedule Successfully deleted!");
            dispatch(getAllItemSchedulesData());
            setOpenDeleteItemScheduleAlert(false);
            setCurItemScheduleId("");
            openItemScheduleView("");
          }
        },
      })
    );
  };

  /**
   * This function will render the itemSchedules
   */
  function renderItemSchedulesList(itemSchedulesList: any): React.JSX.Element {
    return (
      !!itemSchedulesList?.length &&
      itemSchedulesList.map((itemSchedule: any, index: number) => {
        return (
          <div
            className={`itemSchedule-item flex items-center justify-between pr-[68px] pb-[16px] relative cursor-pointer ${
              itemSchedule?.mid === itemScheduleId
                ? "bg-black/5 border-0 border-l-[6px] !border-black pl-[19px] pt-[17px]"
                : "pt-[16px] pl-[24px] border border-[#ECECEC] border-b-0 last:border-b"
            }`}
            key={index}
            onClick={(e) => {
              e.stopPropagation();
              onItemScheduleClick(itemSchedule?.mid);
            }}
          >
            <h1
              className={`text-[18px] mr-[12px] capitalize ${
                itemSchedule?.mid === itemScheduleId ? "font-semibold" : ""
              }`}
            >
              {itemSchedule.name}
            </h1>

            <p className="text-[15px] text-black/50 font-normal">
              {`${
                (itemSchedule?.channelDefIds?.length &&
                  allChannelsDefDataMap[itemSchedule?.channelDefIds[0]]
                    ?.name) ||
                ""
              }
                ${
                  itemSchedule?.channelDefIds?.length > 1
                    ? ` + ${itemSchedule?.channelDefIds?.length} Channels`
                    : ""
                }
              `}
            </p>
            <div
              className="delete-icon rista-sprite ic-delete-grey absolute right-[24px] hidden"
              onClick={(e) => onDeleteIconClick(e, itemSchedule.mid)}
            ></div>
          </div>
        );
      })
    );
  }

  function renderSearchResults(): React.JSX.Element {
    return searchData.results.length ? (
      <>
        <div className="text-14 font-normal pb-16 pl-40 opacity-50 border-b-[1px] border-[#ECECEC]">
          {searchData.results.length} results found
        </div>
        {renderItemSchedulesList(searchData.results)}
      </>
    ) : (
      <div className="flex flex-col justify-center items-center w-full mt-56">
        <div className="flex items-center justify-center w-[72px] h-[72px] bg-opacity-5 bg-[#000] rounded-[8px]">
          <div className="rista-sprite ic-not-found"></div>
        </div>
        <p className="text-20 font-semibold mt-24 mb-6 opacity-40">
          No results found
        </p>
        <p className="text-13 font-normal mb-6 opacity-40 mt-[0px]">
          Please try using other name or keyword
        </p>
      </div>
    );
  }

  const onDeleteIconClick = (e: any, id: string) => {
    setOpenDeleteItemScheduleAlert(true);
    e.stopPropagation();
    setCurItemScheduleId(id);
  };

  const onDeleteAlertClose = () => {
    setOpenDeleteItemScheduleAlert(false);
    setCurItemScheduleId("");
  };

  const commonProps = {
    data,
    modeConfig,
    setModeConfig,
    updateItemScheduleDataInParentState,
    itemScheduleId,
    errorMessageMap,
    onSaveCtaClick,
    onDiscardCtaClick,
    isChangesMade,
  };

  const renderAddNewItemScheduleModal = () => {
    return (
      <Modal open={true} onClose={onDiscardCtaClick}>
        <div className="absolute w-[636px] h-[668px] rounded-[8px] overflow-hidden left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 bg-white">
          <div className="flex items-center justify-between pt-[16px] pl-[24px] pr-[12px]">
            <h1 className="text-[22px] font-semibold">Add New Schedule</h1>
            <div
              className="rista-sprite ic-cross-black"
              onClick={onDiscardCtaClick}
            ></div>
          </div>
          <div className="w-[636px] h-[542px] overflow-y-auto">
            <ItemScheduleDetailsForm {...commonProps} />
          </div>
          <div className="absolute bottom-[0px] w-full z-1 right-0 bg-white h-[70px] px-24 flex justify-end border-t border-[#EBEBEB] items-center overflow-hidden sm:w-full">
            <CtaComponent
              ctaText={"Discard"}
              ctaType={CTA_TYPE.secondary_cta}
              ctaSize={CTA_SIZE.medium}
              onCtaClick={onDiscardCtaClick}
              customClass={"w-[96px] mr-10"}
            ></CtaComponent>
            <CtaComponent
              ctaText={"Save"}
              ctaType={CTA_TYPE.active_cta}
              ctaSize={CTA_SIZE.medium}
              onCtaClick={onSaveCtaClick}
              customClass={"w-[80px] ml-10"}
              isDisabled={!isChangesMade}
            ></CtaComponent>
          </div>
        </div>
      </Modal>
    );
  };

  const onAddButtonClick = () => {
    openItemScheduleView("");
    setModeConfig({ type: COMPONENT_MODES.addNewMode });
  };

  return (
    <section className="bg-white w-[50%] min-h-[100vh] itemSchedules-view-container relative">
      {Object.values(itemSchedulesDataMap)?.length ? (
        <>
          <DescriptionText
            descriptionText={CMS_DESCRIPTION_TEXTS.masterLevel.itemSchedules}
            customClass="text-14 text-black/50 m-[24px] mb-8"
          />
          <div className="action-container p-24 flex justify-between">
            <SearchInputComponent
              placeholder={"Search Item Schedules"}
              customClass={"text-15 py-12"}
              onSerachInput={onSearchInput}
              searchMinLength={3}
              regexCheck={removeSpecialCharacters}
              elementWidthClass={"w-[60%]"}
            ></SearchInputComponent>
            <div className="flex border-[#7FB9FD] border-[1px] bg-white cursor-pointer rounded-[8px] items-center pr-4">
              <p
                className="flex text-blue text-16 font-semibold px-16 py-10"
                onClick={onAddButtonClick}
              >
                <span>+</span>&nbsp;Add
              </p>
            </div>
          </div>
          <div className="h-[calc(100vh-312px)] overflow-y-auto pb-[8px]">
            {searchData.value.length >= 3
              ? renderSearchResults()
              : renderItemSchedulesList(
                  itemSchedulesDataMap
                    ? Object.values(itemSchedulesDataMap)
                    : []
                )}
          </div>
        </>
      ) : (
        <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 flex flex-col justify-center items-center w-full p-24 text-center">
          <img src={emptyListPlaceholder} alt="" />
          <h1 className="mt-[14px] text-[#4B4B4B] text-20 mb-[16px] font-bold">
            Feels So Empty !
          </h1>
          <p className="text-[15px] text-black/70 mb-[28px]">
            You haven’t added any item schedules. Click on ‘+ Add Item Schedule’
            to start adding item schedules.
          </p>
          <CtaComponent
            ctaSize={CTA_SIZE.medium}
            ctaType={CTA_TYPE.active_cta}
            ctaText="+ Add Item Schedule"
            onCtaClick={onAddButtonClick}
          />
        </div>
      )}

      {modeConfig.type === COMPONENT_MODES.addNewMode &&
        renderAddNewItemScheduleModal()}

      {openDeleteItemScheduleAlert && (
        <ConfirmationModal
          onModalClose={onDeleteAlertClose}
          onConfirmBtnClick={() => deleteItemSchedule(curItemScheduleId)}
          headingText="Delete Item Schedule"
          description="This is an irreversible action. Do you still want to continue?"
        />
      )}
    </section>
  );
};

export default ItemScheduleViewComponent;
